import { PaginationComponent, PaginationComponentConfig, PaginationMode } from "appworks/components/ui/pagination-component";
import { ButtonElement, ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { AbstractBetService } from "appworks/services/bet/abstract-bet-service";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { slotDefinition } from "slotworks/model/slot-definition";
import { slotModel } from "slotworks/model/slot-model";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export enum PayoutMode {
    LINES = "lines",
    WAYS = "ways",
    FIXED = "fixed",
    MULTIPLIER_ONLY = "multiplierOnly"
}

export interface PaytableComponentConfig extends PaginationComponentConfig {
    // Automatically set Layers.Background to "paytable" scene when paytable is shown, and "default" when it's hidden
    autoShowBackground: boolean;
    payoutMode: PayoutMode;
    // The minimum bet in a ways game (if applicable)
    waysBaseBet?: number;
    prependPayoutMatches: boolean;

    showTrailingDecimalsOnPayouts: boolean;
    forceCreditModeOnPayouts: boolean;
}

export class PaytableComponent extends PaginationComponent {

    protected showTrailingDecimalsOnPayouts: boolean;
    protected forceCreditModeOnPayouts: boolean;

    protected config: PaytableComponentConfig = {
        autoShowBackground: false,
        payoutMode: PayoutMode.LINES,
        prependPayoutMatches: false,
        mode: PaginationMode.PAGIFY,
        paginationLayerOrder: "default",
        showTrailingDecimalsOnPayouts: false,
        forceCreditModeOnPayouts: false,
        waysBaseBet: 1
    };

    constructor(config?: Partial<PaytableComponentConfig>) {
        super(config);

        if (config) {
            this.config = { ...this.config, ...config };
        }

        Services.get(AbstractBetService).onBetChange.add(() => this.updatePayouts());

        this.uiLayer.onSceneEnter.add((scene: string) => {
            if (scene === "paytable") {
                this.show();
            }
        });

        this.uiLayer.beforeSceneExit.add((scene: string) => {
            if (scene === "paytable") {
                this.hide();
            }
        });

        if (this.contentLayer) {
            this.contentLayer.onSceneEnter.add((scene: string) => {
                if (scene !== "default") {
                    this.update();
                }
            });
        }
    }

    protected update() {
        super.update();

        this.updatePayouts();
    }

    protected onShow() {
        if (this.shouldGeneratePagePips()) {
            this.generatePagePips();
        }

        if (this.config.autoShowBackground && Layers.get("Background").hasScene("paytable")) {
            Layers.get("Background").setScene("paytable").execute();
        }
        super.onShow();
    }

    protected onHide() {
        if (this.config.autoShowBackground && Layers.get("Background").hasScene("paytable")) {
            Layers.get("Background").defaultScene().execute();
        }
        super.onHide();
    }

    protected shouldGeneratePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        return Boolean(btnCentre && btnOffset);
    }

    protected generatePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        const buttonDistance = {
            landscape: Math.abs(btnOffset.landscape.x - btnCentre.landscape.x),
            portrait: Math.abs(btnOffset.portrait.x - btnCentre.portrait.x)
        }
        const centreOffset = {
            landscape: ((buttonDistance.landscape * this.totalPages) / 2) - (buttonDistance.landscape / 2),
            portrait: ((buttonDistance.portrait * this.totalPages) / 2) - (buttonDistance.portrait / 2)
        }

        for (let i = 0; i < this.totalPages; i++) {
            const pageIndex = i;
            const pageName = this.contentSceneNames[pageIndex];

            const button = btnCentre.clone(pageName);
            this.uiLayer.add(button);
            this.uiLayer.addButton(pageName, button);

            button.landscape.x += (buttonDistance.landscape * i) - centreOffset.landscape;
            button.portrait.x += (buttonDistance.portrait * i) - centreOffset.portrait;

            const flexiButton = this.uiLayer.getFlexiButton(pageName);

            button.setEnabled(true);
            const listener = () => this.jumpToPage(pageIndex);
            button.on(ButtonEvent.CLICK, listener);
            this.pageJumpListeners.set(flexiButton, listener);

            if (pageIndex === this.page) { button.setEnabled(false); }
        }

        btnCentre.destroy(); // These were only needed as a reference
        btnOffset.destroy();
    }

    protected updatePayouts() {
        const symbols = slotDefinition.symbolDefinitions;
        // TODO: Why is this read and never used?
        const rtp = slotModel.read().regulations.rtp;

        if (!this.contentLayer || !this.contentLayer.getCurrentScene()) {
            return;
        }

        if (symbols) {
            for (const symbol of symbols) {
                const payoutsText = this.contentLayer.getFlexiText("payouts_" + symbol.id);

                if (payoutsText) {
                    let payoutText = "";
                    if (symbol.payouts) {
                        let output;
                        (symbol.payouts as Map<string, number>).forEach((payout) => {
                            output = this.getPayoutValue(payout);

                            if (payoutText) {
                                payoutText = output + "\n" + payoutText;
                            } else {
                                payoutText = output;
                            }
                        });
                    }

                    payoutsText.setText(payoutText);
                }

                if (symbol.payouts) {
                    symbol.payouts.forEach((payout, matches) => {
                        const payoutText = this.contentLayer.getFlexiText("payouts_" + symbol.id + "_" + matches);

                        const payoutValue = this.getPayoutValue(payout);
                        const matchesLabel = this.config.prependPayoutMatches ? `x${matches}: ` : "";

                        if (payoutText) {
                            if (`${payout}`.includes("respin")) {
                                const numRespins = parseInt(`${payout}`);
                                const suffix = Services.get(TranslationsService).get((numRespins === 1) ? "respin" : "respins").toLocaleUpperCase();
                                payoutText.setText(`${matchesLabel}${numRespins} ${suffix}`);
                            } else {
                                payoutText.setText(`${matchesLabel}${payoutValue}`);
                            }
                        }
                    });
                }
            }
        }
    }

    protected getPayoutValue(payout: number): string {
        let amount;
        let toReturn = "";

        const stakePerLine = Services.get(SlotBetService).getCreditPerLine() * Services.get(SlotBetService).getCreditSize();

        if (this.config.payoutMode === PayoutMode.FIXED) {
            const betPerLine = (Services.get(SlotBetService).getFixedBetCost() / Services.get(SlotBetService).getSelectedLines());
            amount = payout * stakePerLine * betPerLine;
        } else if (this.config.payoutMode === PayoutMode.LINES) {
            amount = payout * stakePerLine;
        } else if (this.config.payoutMode === PayoutMode.WAYS) {
            amount = payout * Services.get(SlotBetService).getTotalStake() / this.config.waysBaseBet;
        } else {
            toReturn = payout.toString();
        }

        if (amount !== undefined) {
            // Format final amount if applicable, and round down in case of fractional "pence" wins
            toReturn = Services.get(CurrencyService).format(Math.floor(amount * 100) / 100, this.showTrailingDecimalsOnPayouts, this.forceCreditModeOnPayouts);
        }

        return toReturn.toString();
    }
}
